import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isIOS, isAndroid } from 'react-device-detect';
import { ACTIVE_URL } from '../../constants';
import { STORE_URLS } from '../../utils/app-download-button';
import { CampaignLanding } from './CampaignLanding';
import { backendPath, type Campaign } from './redirects';

// Only used when a campaign has been toggled inactive in the backend;
// everything else lands on the campaign's own landing page or the store.
const FALLBACK_PATH = '/promo-and-rewards';

const ANDROID_PACKAGE = 'com.padu.paduapp';

// Custom URL scheme the published app registers for deep links.
const APP_SCHEME = 'paduapp';

/** host/path part of the in-app destination, e.g. "promo/betsson?id=X" */
function appDestination(campaign: Campaign, campaignId: string): string | null {
  switch (campaign.type) {
    case 'partner':
      return campaignId
        ? `promo/${campaign.slug}?id=${encodeURIComponent(campaignId)}`
        : `promo/${campaign.slug}`;
    case 'couponset':
      return `couponset/${campaign.slug}`;
    case 'download':
      return null; // no app deep link — go straight to the store
  }
}

/** `path` is the full campaign path as declared in redirects.ts, e.g. "/app/padu/for-you/coupons" */
export function RedirectPage({ campaign, path }: { campaign: Campaign; path: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('id') ?? '';
  const [showLanding, setShowLanding] = useState(false);
  // QrRedirect rows in the backend are keyed by the last path segment
  const backendKey = backendPath(path);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const res = await fetch(`${ACTIVE_URL}/api/qr-redirects/`);
        const redirects: { path: string; active: boolean }[] = await res.json();
        const entry = redirects.find((r) => r.path === backendKey);
        if (entry && !entry.active) {
          if (!cancelled) navigate(FALLBACK_PATH, { replace: true });
          return;
        }
      } catch {
        // If the check fails, fall through and treat the campaign as active.
      }
      if (cancelled) return;

      fetch(`${ACTIVE_URL}/api/redirect-click/`, {
        method: 'POST',
        // Survive the immediate navigation to the deep link / store
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app: 'padu_app',
          user_agent: navigator.userAgent,
          redirect_from: path,
          campaign_id: campaign.type === 'couponset' ? campaign.slug : campaignId,
        }),
      }).catch(() => {});

      if (isIOS || isAndroid) {
        const storeUrl = isIOS ? STORE_URLS.ios : STORE_URLS.android;
        // Deep-linking disabled: the published app registers neither `padu://`
        // nor `paduapp://` (both tested → open the store, not the app), so send
        // everyone straight to the store. Re-enable the block below and set
        // APP_SCHEME once the published app ships a working custom scheme.
        window.location.href = storeUrl;
        return;

        /* Deep-link into the app when installed, else fall back to the store.
        const destination = appDestination(campaign, campaignId);
        if (!destination) {
          window.location.href = storeUrl;
          return;
        }
        if (isAndroid) {
          // Let Android resolve installed-vs-not itself: opens the app when
          // present, otherwise navigates to the store fallback. A JS timer
          // can't know this and misfires when Chrome blocks the scheme.
          window.location.href =
            `intent://${destination}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
            `S.browser_fallback_url=${encodeURIComponent(storeUrl)};end`;
          return;
        }
        window.location.href = `${APP_SCHEME}://${destination}`;
        setTimeout(() => {
          // If the page went hidden, the deep link opened the app —
          // don't drag the backgrounded browser tab to the store.
          if (cancelled || document.hidden) return;
          window.location.href = storeUrl;
        }, 1500);
        */
      } else if (!cancelled) {
        setShowLanding(true);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [campaign, path, campaignId, navigate]);

  return showLanding ? <CampaignLanding content={campaign} /> : null;
}
